import { AgentTile } from '@/types/learning-path';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon } from '@iconify/react';
import { Button } from '@nextui-org/react';

interface SortableAgentCardProps {
  agent: AgentTile;
  index: number;
  onRemove: (id: number) => void;
}

const cardColors = [
  '#F6F0EB',
  '#F4E2DC',
  '#EBE5E5',
  '#F6F0EB',
  '#F4E2DC',
  '#F6F0EB',
  '#F6F0EB',
  '#F4E2DC',
  '#EBE5E5',
];

let lastColor = '';

const getRandomColor = () => {
  let randomColor;
  do {
    randomColor = cardColors[Math.floor(Math.random() * cardColors.length)];
  } while (randomColor === lastColor);
  lastColor = randomColor;
  return randomColor;
};

const SortableAgentCard = ({
  agent,
  index,
  onRemove,
}: SortableAgentCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: agent.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const bgColor = getRandomColor();

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div
        className={`group rounded-large p-3 cursor-grab h-[100px] flex flex-col border-1 border-default-200 ${
          isDragging ? 'shadow-lg ring-2 ring-primary' : ''
        }`}
        style={{ backgroundColor: bgColor }}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <div className="opacity-1 sm:opacity-0 group-hover:opacity-100 transition-opacity">
              <Icon
                icon="lucide:grip-vertical"
                className="text-default-600 w-4 h-4"
              />
            </div>
            <span className="text-default-600 text-sm">{index + 1}.</span>
          </div>
          <Button
            isIconOnly
            size="sm"
            variant="light"
            className="opacity-1 sm:opacity-0 group-hover:opacity-100 transition-opacity bg-white/20 min-w-unit-6 w-6 h-6"
            onPress={() => onRemove(agent.id)}
          >
            <Icon
              icon="lucide:trash-2"
              className="text-default-600 w-3.5 h-3.5"
            />
          </Button>
        </div>

        <div className="flex-1 flex flex-col justify-between mt-1">
          <h4 className="text-sm font-semibold text-default-800 truncate">
            {agent.name}
          </h4>
          <div className="flex items-center gap-1.5">
            <Icon
              icon="lucide:message-circle"
              className="text-default-600 w-3.5 h-3.5"
            />
            <span className="text-xs text-default-600">6 Min</span>
          </div>
        </div>
      </div>
    </div>
  );
};

SortableAgentCard.displayName = 'SortableAgentCard';

export { SortableAgentCard };
